<script setup>
import { computed, ref, watch } from 'vue';
import Spinner from '../spinner/Spinner.vue';
import ImageErrorFallback from './ImageErrorFallback.vue';
import {
  isVideoMediaType,
  normalizeMediaItems,
  MEDIA_TYPES,
} from './galleryUtils';

const props = defineProps({
  items: { type: Array, default: () => [] },
  modelValue: { type: Number, default: 0 },
  loading: { type: Boolean, default: false },
  emptyMessage: { type: String, default: 'Sem fotos disponíveis' },
  showThumbs: { type: Boolean, default: true },
  mediaTabs: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue', 'open', 'select-tab']);

const internalIndex = ref(props.modelValue);
const touchStartX = ref(null);
const mouseStartX = ref(null);
const mouseMoved = ref(false);
const suppressOpen = ref(false);
const imageErrors = ref({});
const mediaItems = computed(() => normalizeMediaItems(props.items));
const activeItem = computed(() => mediaItems.value[internalIndex.value]);
const canNavigate = computed(() => mediaItems.value.length > 1);
const hiddenCount = computed(() => Math.max(mediaItems.value.length - 5, 0));
const visibleThumbs = computed(() => mediaItems.value.slice(0, 5));
const coverLabel = 'Capa';
const previousLabel = 'Foto anterior';
const nextLabel = 'Próxima foto';
const counterText = computed(
  () => `${internalIndex.value + 1} / ${mediaItems.value.length}`
);
const activeItemIsVideo = computed(() =>
  isVideoMediaType(activeItem.value?.type)
);
const activeVideoLabel = computed(() =>
  activeItem.value?.type === MEDIA_TYPES.VIDEO_360
    ? 'Abrir tour 360°'
    : 'Abrir vídeo'
);
const hiddenCountLabel = computed(() => `+${hiddenCount.value}`);
const openPhotoLabel = index => `Abrir foto ${index + 1}`;
const viewPhotoLabel = index => `Ver foto ${index + 1}`;
const mediaTabLabel = tab =>
  `${tab.label}${tab.count ? ` (${tab.count})` : ''}`;
const hasImageError = item => Boolean(item?.src && imageErrors.value[item.src]);

const goTo = index => {
  const length = mediaItems.value.length;
  if (!length) return;

  const nextIndex = Math.max(0, Math.min(index, length - 1));
  internalIndex.value = nextIndex;
  emit('update:modelValue', nextIndex);
};

const openActiveItem = () => {
  if (suppressOpen.value) return;
  emit('open', { index: internalIndex.value, item: activeItem.value });
};

const handleKeydown = event => {
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    goTo(internalIndex.value - 1);
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    goTo(internalIndex.value + 1);
  } else if (event.key === 'Enter') {
    emit('open', { index: internalIndex.value, item: activeItem.value });
  }
};

const handleTouchStart = event => {
  touchStartX.value = event.touches?.[0]?.clientX ?? null;
};

const handleTouchEnd = event => {
  if (touchStartX.value == null) return;
  const endX = event.changedTouches?.[0]?.clientX ?? touchStartX.value;
  const delta = endX - touchStartX.value;
  touchStartX.value = null;
  if (Math.abs(delta) < 36) return;
  goTo(delta > 0 ? internalIndex.value - 1 : internalIndex.value + 1);
};

const handleMouseDown = event => {
  if (event.button !== 0) return;
  mouseStartX.value = event.clientX;
  mouseMoved.value = false;
};

const handleMouseMove = event => {
  if (mouseStartX.value == null) return;
  if (Math.abs(event.clientX - mouseStartX.value) > 5) {
    mouseMoved.value = true;
  }
};

const handleMouseUp = event => {
  if (mouseStartX.value == null) return;
  const delta = event.clientX - mouseStartX.value;
  mouseStartX.value = null;

  if (Math.abs(delta) >= 36) {
    goTo(delta > 0 ? internalIndex.value - 1 : internalIndex.value + 1);
  }

  if (mouseMoved.value) {
    suppressOpen.value = true;
    window.setTimeout(() => {
      suppressOpen.value = false;
    }, 0);
  }
};

const markImageError = item => {
  if (!item?.src) return;
  imageErrors.value = { ...imageErrors.value, [item.src]: true };
};

watch(
  () => props.modelValue,
  value => {
    internalIndex.value = Math.min(
      Math.max(Number(value) || 0, 0),
      Math.max(mediaItems.value.length - 1, 0)
    );
  }
);

watch(
  () => mediaItems.value.length,
  length => {
    if (internalIndex.value >= length) {
      goTo(Math.max(length - 1, 0));
    }
  }
);
</script>

<template>
  <section class="w-full">
    <div
      v-if="loading"
      class="flex aspect-[16/10] items-center justify-center rounded-lais bg-lais-bg-muted text-lais-primary"
    >
      <Spinner :size="24" />
    </div>

    <div
      v-else-if="!mediaItems.length"
      class="flex aspect-[16/10] items-center justify-center rounded-lais border border-dashed border-lais-border bg-lais-bg-muted px-4 text-center text-lais-body-sm text-lais-text-helper"
    >
      {{ emptyMessage }}
    </div>

    <div v-else class="space-y-2">
      <div
        class="relative aspect-[16/10] overflow-hidden rounded-lais bg-black outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-lais-primary-50"
        tabindex="0"
        @keydown="handleKeydown"
        @touchstart.passive="handleTouchStart"
        @touchend.passive="handleTouchEnd"
        @mousedown="handleMouseDown"
        @mousemove="handleMouseMove"
        @mouseup="handleMouseUp"
        @mouseleave="handleMouseUp"
      >
        <button
          type="button"
          class="block size-full"
          :aria-label="openPhotoLabel(internalIndex)"
          @click="openActiveItem"
        >
          <span
            v-if="activeItemIsVideo"
            class="flex size-full flex-col items-center justify-center gap-3 bg-black text-white"
          >
            <span
              class="grid size-14 place-items-center rounded-full bg-white/90 text-lais-primary"
            >
              <span
                :class="
                  activeItem?.type === MEDIA_TYPES.VIDEO_360
                    ? 'i-lucide-rotate-3d'
                    : 'i-lucide-play'
                "
                class="size-7"
                aria-hidden="true"
              />
            </span>
            <span class="text-[13px] font-semibold">
              {{ activeVideoLabel }}
            </span>
          </span>
          <ImageErrorFallback
            v-else-if="!activeItem?.src || hasImageError(activeItem)"
          />
          <img
            v-else
            :src="activeItem.src"
            :alt="activeItem.alt"
            class="size-full object-cover"
            :loading="internalIndex === 0 ? 'eager' : 'lazy'"
            :fetchpriority="internalIndex === 0 ? 'high' : 'auto'"
            @error="markImageError(activeItem)"
          />
        </button>

        <span
          v-if="activeItem?.isCover"
          class="absolute left-3 top-3 rounded-full bg-white px-2.5 py-1 text-lais-label-sm font-semibold text-lais-primary shadow-lais-card"
        >
          {{ coverLabel }}
        </span>

        <div
          v-if="mediaTabs.length > 1"
          class="absolute bottom-3 left-3 z-10 flex max-w-[70%] flex-wrap gap-1.5"
        >
          <button
            v-for="tab in mediaTabs"
            :key="tab.id"
            type="button"
            class="rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-semibold text-white transition hover:bg-lais-primary"
            @click.stop="emit('select-tab', tab.id)"
          >
            {{ mediaTabLabel(tab) }}
          </button>
        </div>

        <button
          v-if="canNavigate"
          type="button"
          class="absolute left-3 top-1/2 inline-flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-lais-text shadow-lais-card transition hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
          :aria-label="previousLabel"
          @click.stop="goTo(internalIndex - 1)"
        >
          <span class="i-lucide-chevron-left size-5" aria-hidden="true" />
        </button>
        <button
          v-if="canNavigate"
          type="button"
          class="absolute right-3 top-1/2 inline-flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-lais-text shadow-lais-card transition hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
          :aria-label="nextLabel"
          @click.stop="goTo(internalIndex + 1)"
        >
          <span class="i-lucide-chevron-right size-5" aria-hidden="true" />
        </button>

        <span
          class="absolute bottom-3 right-3 rounded-full bg-black/70 px-2.5 py-1 text-lais-label-sm font-semibold text-white"
        >
          {{ counterText }}
        </span>
      </div>

      <div v-if="showThumbs" class="grid grid-cols-5 gap-2">
        <button
          v-for="(item, index) in visibleThumbs"
          :key="item.id"
          type="button"
          class="relative aspect-[4/3] overflow-hidden rounded-lais border transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-lais-primary-50"
          :class="
            index === internalIndex
              ? 'border-lais-primary'
              : 'border-lais-border'
          "
          :aria-label="viewPhotoLabel(index)"
          :aria-current="index === internalIndex"
          @click="goTo(index)"
        >
          <img
            v-if="
              item.src && !hasImageError(item) && !isVideoMediaType(item.type)
            "
            :src="item.src"
            :alt="item.alt"
            class="size-full object-cover"
            loading="lazy"
            @error="markImageError(item)"
          />
          <span
            v-else-if="isVideoMediaType(item.type)"
            class="flex size-full items-center justify-center bg-black text-white"
          >
            <span class="i-lucide-play size-5" aria-hidden="true" />
          </span>
          <span
            v-else
            class="flex size-full items-center justify-center bg-lais-bg-muted text-lais-icon"
          >
            <span class="i-lucide-image-off size-5" aria-hidden="true" />
          </span>
          <span
            v-if="hiddenCount && index === visibleThumbs.length - 1"
            class="absolute inset-0 flex items-center justify-center bg-black/65 text-lais-heading-sm font-semibold text-white"
          >
            {{ hiddenCountLabel }}
          </span>
        </button>
      </div>
    </div>
  </section>
</template>
